import { ReactNode } from "react";
import usePermissions from "../hooks/usePermissions";
import { Permission } from "../lib/permissions";

export interface CanProps {
  /**
   * The permission(s) required to render the children. When an array is
   * provided, by default the user must hold ALL of them (logical AND). Set
   * `requireAll={false}` to render when the user holds ANY of them.
   */
  perform: Permission | Permission[];
  /** When true (default) require all listed permissions; otherwise require any. */
  requireAll?: boolean;
  /** Rendered when the user lacks the required permission(s). Defaults to null. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * `<Can>` conditionally renders its children based on the current user's RBAC
 * permissions. Protected UI never mounts when the user lacks access.
 *
 * @example
 *   <Can perform="device:write">
 *     <AddDeviceButton />
 *   </Can>
 */
export default function Can({
  perform,
  requireAll = true,
  fallback = null,
  children,
}: CanProps) {
  const { can } = usePermissions();

  const required = Array.isArray(perform) ? perform : [perform];
  const allowed = requireAll ? can(required) : required.some((p) => can(p));

  return <>{allowed ? children : fallback}</>;
}
